import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";
import { Banknote, CreditCard, AlertCircle, CheckCircle } from "lucide-react";

interface CashPaymentSuggestionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProceedWithCash: () => void;
  onCloseCart: () => void;
  totalAmount: number;
  paymentMethod: "Cash" | "Digital" | null;
}

export function CashPaymentSuggestionModal({
  open,
  onOpenChange,
  onProceedWithCash,
  onCloseCart,
  totalAmount,
  paymentMethod,
}: CashPaymentSuggestionModalProps) {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleProceedWithCash = async () => {
    setIsProcessing(true);
    try {
      await onProceedWithCash();
      onOpenChange(false);
    } catch (error) {
      console.error("Error proceeding with cash payment:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseCart = () => {
    onCloseCart();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-orange-500" />
            {t("paymentIssue", "Payment Issue")}
          </DialogTitle>
          <DialogDescription>
            {t("paymentIssueDescription", "We encountered an issue with your digital payment. Don't worry, your cart is still available!")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("digitalPaymentFailed", "Digital Payment Failed")}</AlertTitle>
            <AlertDescription>
              {t("digitalPaymentFailedMessage", "Your digital payment was not completed successfully. This could be due to network issues, card problems, or payment cancellation.")}
            </AlertDescription>
          </Alert>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">{t("yourOrder", "Your Order")}</h4>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{t("totalAmount", "Total Amount")}</span>
              <span className="font-semibold text-lg">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'EUR'
                }).format(totalAmount)}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-800">{t("cashPaymentAvailable", "Cash Payment Available")}</h4>
                <p className="text-sm text-green-700">
                  {t("cashPaymentDescription", "You can still complete your order using cash payment. Your items are ready to be prepared!")}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <CreditCard className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-800">{t("tryAgainLater", "Try Again Later")}</h4>
                <p className="text-sm text-blue-700">
                  {t("tryAgainLaterDescription", "You can also try digital payment again later if you prefer.")}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4">
            <Button
              onClick={handleProceedWithCash}
              disabled={isProcessing}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
            >
              <Banknote className="mr-2 h-4 w-4" />
              {isProcessing ? t("processing", "Processing...") : t("proceedWithCash", "Proceed with Cash Payment")}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleCloseCart}
              className="w-full"
            >
              {t("closeCart", "Close Cart")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 